/**
 * INTELLIGENCE SERVICE — Detecció de patrons i anomalies
 * Analitza dades dels últims 7 dies per detectar situacions problemàtiques.
 */
import hosys from "@/api/api";
import moment from "moment";

/**
 * Obté dates dels últims N dies en format YYYY-MM-DD
 */
function lastNDays(n) {
  const days = [];
  for (let i = 0; i < n; i++) {
    days.push(moment().subtract(i, "days").format("YYYY-MM-DD"));
  }
  return days;
}

/**
 * Analitza temperatures de la setmana per detectar equips problemàtics
 */
async function analyzeTemperatures(venueId) {
  const days = lastNDays(7);
  const allTemps = await Promise.all(
    days.map(d => hosys.entities.TemperatureRecord.filter({ venue_id: venueId, date: d }))
  );
  const temps = allTemps.flat();

  const incorrectByEquip = {};
  const totalByEquip = {};

  temps.forEach(t => {
    const key = t.equipment_name;
    totalByEquip[key] = (totalByEquip[key] || 0) + 1;
    if (t.status === "incorrect") {
      incorrectByEquip[key] = (incorrectByEquip[key] || 0) + 1;
    }
  });

  const issues = [];
  Object.entries(incorrectByEquip).forEach(([equip, count]) => {
    const total = totalByEquip[equip] || 1;
    const pct = Math.round((count / total) * 100);
    if (count >= 2) {
      issues.push({
        type: "temperature_pattern",
        severity: count >= 4 ? "critical" : "warning",
        equipment: equip,
        count,
        pct,
        message: `${equip} ha registrat ${count} temperatures fora de rang els últims 7 dies (${pct}% dels controls)`,
        recommendation: "Revisar l'equip de refrigeració i contactar amb el servei tècnic si és necessari.",
        link: "Temperatures",
      });
    }
  });

  const recordsByDay = {};
  days.forEach(d => { recordsByDay[d] = 0; });
  temps.forEach(t => { if (t.date) recordsByDay[t.date] = (recordsByDay[t.date] || 0) + 1; });
  const daysWithoutRecords = Object.entries(recordsByDay).filter(([, count]) => count === 0).length;
  if (daysWithoutRecords >= 2) {
    issues.push({
      type: "missing_temp_records",
      severity: "warning",
      message: `Hi ha ${daysWithoutRecords} dies sense registres de temperatura els últims 7 dies`,
      recommendation: "Assegura't de registrar temperatures almenys un cop al dia.",
      link: "Temperatures",
    });
  }

  return { issues, totalRecords: temps.length, incorrectCount: Object.values(incorrectByEquip).reduce((a, b) => a + b, 0) };
}

/**
 * Analitza neteges per detectar tasques pendents recurrents
 */
async function analyzeCleaning(venueId) {
  const days = lastNDays(7);
  const allCleaning = await Promise.all(
    days.map(d => hosys.entities.CleaningRecord.filter({ venue_id: venueId, date: d }))
  );
  const records = allCleaning.flat();

  const pendingByTask = {};
  const totalByTask = {};

  records.forEach(r => {
    const key = r.task;
    totalByTask[key] = (totalByTask[key] || 0) + 1;
    if (!r.completed) {
      pendingByTask[key] = (pendingByTask[key] || 0) + 1;
    }
  });

  const issues = [];
  Object.entries(pendingByTask).forEach(([task, count]) => {
    const total = totalByTask[task] || 1;
    const pct = Math.round((count / total) * 100);
    if (count >= 2 && pct >= 40) {
      issues.push({
        type: "cleaning_pattern",
        severity: pct >= 70 ? "warning" : "info",
        task,
        count,
        pct,
        message: `La tasca "${task}" queda pendent freqüentment (${count} vegades, ${pct}% dels dies)`,
        recommendation: "Revisar la planificació i assignació d'aquesta tasca de neteja.",
        link: "Cleaning",
      });
    }
  });

  const totalCompleted = records.filter(r => r.completed).length;
  const totalPending = records.filter(r => !r.completed).length;

  return { issues, totalRecords: records.length, totalCompleted, totalPending };
}

/**
 * Analitza incidències per detectar categories recurrents
 */
async function analyzeIncidents(venueId) {
  const weekAgo = moment().subtract(7, "days").format("YYYY-MM-DD");
  const incidents = await hosys.entities.Incident.filter({ venue_id: venueId });

  const recentIncidents = incidents.filter(i => i.date >= weekAgo);
  const openIncidents = incidents.filter(i => i.status !== "closed");

  const byCategory = {};
  recentIncidents.forEach(i => {
    byCategory[i.category] = (byCategory[i.category] || 0) + 1;
  });

  const CATEGORY_LABELS = {
    food_safety: "Seguretat alimentària",
    equipment_failure: "Avaria d'equip",
    temperature_deviation: "Desviació de temperatura",
    cleaning_issue: "Neteja",
    supplier_issue: "Proveïdor",
    staff_issue: "Personal",
    other: "Altres",
  };

  const issues = [];
  Object.entries(byCategory).forEach(([cat, count]) => {
    if (count >= 2) {
      issues.push({
        type: "incident_pattern",
        severity: count >= 3 ? "warning" : "info",
        category: cat,
        count,
        message: `Hi ha ${count} incidències recurrents de "${CATEGORY_LABELS[cat] || cat}" aquesta setmana`,
        recommendation: "Revisar els procediments relacionats i establir mesures preventives.",
        link: "Incidents",
      });
    }
  });

  // Incidències obertes sense acció correctiva > 3 dies
  const staleIncidents = openIncidents.filter(i => {
    const daysOpen = moment().diff(moment(i.date), "days");
    return daysOpen >= 3 && !i.corrective_action;
  });

  if (staleIncidents.length > 0) {
    issues.push({
      type: "stale_incidents",
      severity: "warning",
      count: staleIncidents.length,
      message: `${staleIncidents.length} incidència(es) obertes fa més de 3 dies sense acció correctiva`,
      recommendation: "Assignar accions correctives i tancar les incidències resoltes.",
      link: "Incidents",
    });
  }

  return {
    issues,
    totalThisWeek: recentIncidents.length,
    openCount: openIncidents.length,
    byCategory,
  };
}

/**
 * Analitza verificacions pendents
 */
async function analyzeVerifications(venueId) {
  const today = moment().format("YYYY-MM-DD");
  const verifications = await hosys.entities.Verification.filter({ venue_id: venueId });

  const overdue = verifications.filter(v =>
    v.planned_date < today && v.status === "pending"
  );

  const issues = [];
  if (overdue.length > 0) {
    issues.push({
      type: "overdue_verifications",
      severity: overdue.length >= 3 ? "warning" : "info",
      count: overdue.length,
      message: `${overdue.length} verificació(ons) APPCC pendents i endarrerides`,
      recommendation: "Realitzar les verificacions pendents per mantenir el sistema APPCC al dia.",
      link: "Verifications",
    });
  }

  return { issues, overdueCount: overdue.length, totalCount: verifications.length };
}

/**
 * Funció principal: genera l'anàlisi intel·ligent complet
 */
export async function generateIntelligenceReport(venueId) {
  const [tempAnalysis, cleaningAnalysis, incidentAnalysis, verificationAnalysis] = await Promise.all([
    analyzeTemperatures(venueId),
    analyzeCleaning(venueId),
    analyzeIncidents(venueId),
    analyzeVerifications(venueId),
  ]);

  const allIssues = [
    ...tempAnalysis.issues,
    ...cleaningAnalysis.issues,
    ...incidentAnalysis.issues,
    ...verificationAnalysis.issues,
  ];

  const criticalCount = allIssues.filter(i => i.severity === "critical").length;
  const warningCount = allIssues.filter(i => i.severity === "warning").length;
  const infoCount = allIssues.filter(i => i.severity === "info").length;

  return {
    generatedAt: new Date().toISOString(),
    summary: {
      criticalCount,
      warningCount,
      infoCount,
      totalIssues: allIssues.length,
    },
    temperatures: tempAnalysis,
    cleaning: cleaningAnalysis,
    incidents: incidentAnalysis,
    verifications: verificationAnalysis,
    allIssues,
  };
}